import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import { CurrencyFormat } from '../../../Helpers/Formatters';
import { InfoOutlined as InfoIconOutlined } from '@mui/icons-material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import ProductModalList from '../Common/ProductModalList';
import './FinalExpenseQuoteCard.scss';

const TooltipColor = (props) => (
  <Tooltip
    {...props}
    slotProps={{
      tooltip: {
        sx: (theme) => ({
          backgroundColor: theme.palette.common.white,
          color: 'rgba(0, 0, 0, 0.87)',
          boxShadow: theme.shadows[1],
          fontSize: 11,
          cursor: 'pointer',
        }),
      },
    }}
  />
);

export default function FinalExpenseQuoteCard(props) {

  const productDetailObj = props.quote;

  const [accordionExpanded, setAccordionExpanded] = useState(false);

  return (
    <div className={`final-expense-quote-card ${props.isFeatured ? "featuredBackground" : 'light-bg'}`}>
      <Accordion onChange={(obj, card) => setAccordionExpanded(card)} square>
        <AccordionSummary className="accordionButton" expandIcon={<ExpandMoreIcon />}>
          <div className="product-summary">
            <div className="logo-container">
              <img className="carrier-logo-lg" src={props.logoUrl} alt={`${props.carrierName}`} />
            </div>
            <div className="ratings-container">
              <TooltipColor title="Based on independent opinions from AM Best">
                <span className="carrier-rating primary-color">
                  {props.rating}&nbsp;Rated&nbsp;Carrier&nbsp;
                  <InfoIconOutlined />
                </span>
              </TooltipColor>
            </div>
            <div className="product-note-container">
              <ul dangerouslySetInnerHTML={{ __html: props.quote.productValueProposition }} />
            </div>
            <div className="rate-container">
              <p className={`price-container`}>
                {props.isQuotingCoverageAmount ? <NumericFormat
                  className="priceColor primary-color primaryDollar"
                  value={props.monthlyRate}
                  displayType={'text'}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                /> :
                  <CurrencyFormat
                    className="priceColor primary-color primaryDollar"
                    number={props.quote.coverageAmount} whole removeSymbol></CurrencyFormat>}
                {props.isQuotingCoverageAmount && <span
                  className="priceText primary-color"> /mo</span>}
              </p>
              {!props.isQuotingCoverageAmount && <p className="monthly-rate primary-color">Coverage at <CurrencyFormat number={props.monthlyRate} whole /> mo</p>}
            </div>
            <div className="action-container">
              <Button
                component="div"
                className={"selectButton background-color-16" + (accordionExpanded ? " accordion-expanded" : "")}
                color="primary"
                variant={accordionExpanded ? "contained" : "outlined"} onClick={(e) => { e.stopPropagation(); props.onClick(e); }} aria-describedby={props.productName}
                id={props.productName}>
                Select
              </Button>
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className="product-details-grid">
            <div className="details-header-container">
              <h2>{productDetailObj.productName}</h2>
            </div>
            <div className="details-content-container">
              <div className="info-container">
                <div className="ratings-container">
                  <h4>Carrier Rating</h4>
                  <TooltipColor title="Based on independent opinions from AM Best">
                    <span className="carrier-rating primary-color">
                      {props.rating}&nbsp;Rated&nbsp;
                      <InfoIconOutlined />
                    </span>
                  </TooltipColor>
                </div>
                <div className="time-container">
                  <h4>Approval Time</h4>
                  <p>{productDetailObj.underwritingTimeTypical}</p>
                </div>
                <div className="product-note-container">
                  <ul dangerouslySetInnerHTML={{ __html: props.quote.productValueProposition }} />
                </div>
              </div>
              <div className="product-info-container">
                <h3>Product Information</h3>
                <div className="modal-list-wrapper">
                  <ProductModalList data={props.productHighlightInfo} />
                </div>
                <div className="product-info-details">
                  <p dangerouslySetInnerHTML={{ __html: props.productDetails.props.productDescription }} />
                  <ul dangerouslySetInnerHTML={{ __html: props.productDetails.props.productInformation }} />
                </div>
              </div>
              <div className="riders-container">
                <h3>Policy Riders</h3>
                {productDetailObj.availableRiders && <p>Some riders are available at no additional cost, some will affect your price.</p>}
                <div className="modal-list-wrapper">
                  {productDetailObj.availableRiders ? <ProductModalList data={productDetailObj.availableRiders} /> : <div className="modal-list-container"><p>No riders are available for this product.</p></div>}
                </div>
              </div>
              <div className="ratings-container">
                <h3>Insurance Carrier Ratings</h3>
                <p>These ratings are performed by independent governing bodies and reflect the reliability of {props.carrierName} as a carrier.</p>
                <div className="rating-cards-wrapper">
                  <ul className="rating-cards">
                    {productDetailObj.ratingAMBest && (
                      <li>
                        <ProductDetailsRatingCard logo="/logos/ratings/ambest.jpg" rater="AM Best" rating={productDetailObj.ratingAMBest} />
                      </li>
                    )}
                    {productDetailObj.ratingSP && (
                      <li>
                        <ProductDetailsRatingCard logo="/logos/ratings/sp.jpg" rater="S&amp;P Global" rating={productDetailObj.ratingSP} />
                      </li>
                    )}
                    {productDetailObj.ratingMoodys && (
                      <li>
                        <ProductDetailsRatingCard logo="/logos/ratings/moodys.jpg" rater="Moody's" rating={productDetailObj.ratingMoodys} />
                      </li>
                    )}
                    {productDetailObj.ratingFitch && (
                      <li>
                        <ProductDetailsRatingCard logo="/logos/ratings/fitch.jpg" rater="Fitch" rating={productDetailObj.ratingFitch} />
                      </li>
                    )}
                    {productDetailObj.ratingComdex && (
                      <li>
                        <ProductDetailsRatingCard logo="/logos/ratings/comdex.jpg" rater="Comdex" rating={productDetailObj.ratingComdex} />
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
            <div className="mobile-cta-container">
              <Button color="primary"
                variant="contained" onClick={props.onClick} aria-describedby={props.productName}
                id={props.productName}>
                Select Product
              </Button>
            </div>
            <div className="details-footer-container">
              {productDetailObj.disclosureDetail && (
                <Fragment>
                  <h3>Disclosure</h3>
                  <p>{productDetailObj.disclosureDetail}</p>
                </Fragment>
              )}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

const ProductDetailsRatingCard = (props) => {
  return (
    <Card className="rating-card">
      <div className="rating-card-head">
        <img src={props.logo} alt={props.rater} className="img-fluid" />
      </div>
      <p className="rater">{props.rater}</p>
      <p className="rating">{props.rating}</p>
    </Card>
  );
};

FinalExpenseQuoteCard.propTypes = {
  carrierName: PropTypes.string.isRequired,
  monthlyRate: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  productDetails: PropTypes.object.isRequired,
};
